import { BlinkColor, NotificationStrategy, NotificationStrategyPayload } from '@ekkogmbh/apisdk';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { createContextHardened } from 'src/Common/Helper/ContextHelper';

interface Context {
  /** The result strategy manipulated by this provider. */
  notificationStrategy?: NotificationStrategyPayload;
  _strategies: readonly NotificationStrategyPayload[];
  /** Adds a strategy. When more than 1 strategies are added, the main object will become a combined strategy */
  addStrategy: (type: NotificationStrategy) => void;
  /** When only one strategy remains, the main object will no longer be of type "combined" */
  removeStrategy: (index: number) => void;
  /** Sets the strategy at a specific index in a combined strategy */
  setStrategy: (index: number, strategy: NotificationStrategyPayload) => void;
  /** Replaces the "root" notification strategy object */
  setStrategies: (startegy?: NotificationStrategyPayload) => void;
}
const [NotificationStrategyContext, useNotificationStrategy] = createContextHardened<Context>('Notification Strategy');
export default useNotificationStrategy;

interface Props {
  children: React.ReactNode;
}
export const NotificationStrategyProvider = ({ children }: Props): React.JSX.Element => {
  const [strategies, setStrategies] = useState<NotificationStrategyPayload[]>([]);

  const addStrategy = useCallback(
    (type: NotificationStrategy) => {
      const strats = [...strategies];
      switch (type) {
        case NotificationStrategy.LIGHT:
          strats.push({
            name: type,
            configuration: {
              color: BlinkColor.RED,
              duration: 30,
              bypassQueue: false,
            },
          });
          break;
        default:
          strats.push({
            name: type,
            configuration: {},
          });
      }
      setStrategies(strats);
    },
    [strategies],
  );

  const removeStrategy = useCallback(
    (index: number) => {
      const strats = [...strategies];
      strats.splice(index, 1);
      setStrategies(strats);
    },
    [strategies],
  );

  const setStrategy = useCallback(
    (index: number, strategy: NotificationStrategyPayload) => {
      if ([NotificationStrategy.COMBINED].includes(strategy.name)) {
        console.error('Adding COMBINED Notification Strategy not allowed');
        return;
      }

      const strats = [...strategies];

      // setting to NULL removes the strategy, when there is more than one
      if (strategy.name === NotificationStrategy.NULL && strats.length > 1) {
        strats.splice(index, 1);
        enqueueSnackbar('Removed NULL-Strategy from combined notification strategy');
      } else {
        strats[index] = strategy;
      }

      setStrategies(strats);
    },
    [strategies],
  );

  const notificationStrategy: NotificationStrategyPayload | undefined = useMemo(() => {
    switch (strategies.length) {
      case 1:
        return strategies[0];
      case 0:
        return undefined;
      default:
        return {
          name: NotificationStrategy.COMBINED,
          configuration: { strategies },
        };
    }
  }, [strategies]);

  const replaceStrategies = useCallback((replacement?: NotificationStrategyPayload) => {
    switch (replacement?.name) {
      case NotificationStrategy.COMBINED:
        setStrategies(replacement.configuration.strategies as NotificationStrategyPayload[]);
        break;
      case NotificationStrategy.NULL:
        setStrategies([{ name: NotificationStrategy.NULL, configuration: {} }]);
        break;
      case undefined:
        setStrategies([]);
        break;
      default:
        setStrategies([replacement!]);
    }
  }, []);

  return (
    <NotificationStrategyContext.Provider
      value={{
        notificationStrategy,
        _strategies: strategies,
        addStrategy,
        removeStrategy,
        setStrategy,
        setStrategies: replaceStrategies,
      }}
    >
      {children}
    </NotificationStrategyContext.Provider>
  );
};
